import { useMemo, useState } from "react";

import { AdminAvatar } from "@/components/admin/avatar";
import { Card, CardHead } from "@/components/admin/card";
import { ChipFilter } from "@/components/admin/chip-filter";
import { PageHead } from "@/components/admin/page-head";
import { Pagination } from "@/components/admin/pagination";
import { Segmented } from "@/components/admin/segmented";
import { Tag } from "@/components/admin/tag";
import {
	useHubs,
	useLogisticsKpi,
	useShipments,
	useTours,
} from "@/hooks/use-logistics";
import { useLogisticsSocket } from "@/hooks/use-logistics-socket";
import { fmtFCFA } from "@/lib/format";
import type { Shipment, ShipmentStatus, Tour } from "@/lib/logistics/types";
import type { TagClass } from "@/lib/status";

type Tab = "preparation" | "tournees" | "kpi";

const PAGE_SIZE = 20;

const SELECT_STYLE: React.CSSProperties = {
	padding: "6px 10px",
	borderRadius: 8,
	border: "1px solid var(--a-border, #ddd)",
	background: "var(--a-bg, #fff)",
	fontSize: 13,
};

const SHIPMENT_STATUS_META: Record<
	ShipmentStatus,
	{ label: string; cls: TagClass }
> = {
	PENDING: { label: "À préparer", cls: "warning" },
	PICKING: { label: "Picking", cls: "info" },
	PACKED: { label: "Emballé", cls: "info" },
	READY_TO_SHIP: { label: "Prêt", cls: "terra" },
	ASSIGNED: { label: "Affecté", cls: "terra" },
	IN_TRANSIT: { label: "En transit", cls: "info" },
	DELIVERED: { label: "Livré", cls: "success" },
	REFUSED: { label: "Refusé", cls: "danger" },
	RETURNED: { label: "Retourné", cls: "neutral" },
	LOST: { label: "Perdu", cls: "danger" },
};

const TOUR_STATUS_META: Record<string, { label: string; cls: TagClass }> = {
	DRAFT: { label: "Brouillon", cls: "neutral" },
	PLANNED: { label: "Planifiée", cls: "info" },
	IN_PROGRESS: { label: "En cours", cls: "terra" },
	COMPLETED: { label: "Terminée", cls: "success" },
	CANCELLED: { label: "Annulée", cls: "danger" },
};

function slaCls(dueIso: string | null, status: ShipmentStatus): TagClass {
	if (["DELIVERED", "RETURNED", "LOST"].includes(status)) return "neutral";
	if (!dueIso) return "neutral";
	const diffMin = (new Date(dueIso).getTime() - Date.now()) / 60000;
	if (diffMin < 0) return "danger";
	if (diffMin < 120) return "warning";
	return "success";
}

function slaLabel(dueIso: string | null): string {
	if (!dueIso) return "—";
	const diffMin = Math.round((new Date(dueIso).getTime() - Date.now()) / 60000);
	if (diffMin < 0) return `Dépassé ${Math.abs(diffMin)} min`;
	if (diffMin < 60) return `${diffMin} min`;
	return `${Math.floor(diffMin / 60)} h ${diffMin % 60} min`;
}

function relAgo(iso: string): string {
	const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
	if (s < 60) return `il y a ${s} s`;
	if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
	return `il y a ${Math.floor(s / 3600)} h`;
}

export function AdminLogisticsPage() {
	const [tab, setTab] = useState<Tab>("preparation");

	return (
		<>
			<PageHead
				title="Logistique"
				sub="Préparation, suivi des tournées temps réel et indicateurs de performance"
			/>
			<Segmented<Tab>
				value={tab}
				onChange={setTab}
				options={[
					{ value: "preparation", label: "File de préparation" },
					{ value: "tournees", label: "Suivi tournées" },
					{ value: "kpi", label: "KPI" },
				]}
			/>
			<div style={{ marginTop: 16 }}>
				{tab === "preparation" && <PreparationTab />}
				{tab === "tournees" && <ToursTab />}
				{tab === "kpi" && <KpiTab />}
			</div>
		</>
	);
}

// ─────────────────────────────────────────────────────────────
// LOG-03 — File de préparation
// ─────────────────────────────────────────────────────────────

const PREP_FILTERS: {
	key: string;
	label: string;
	status?: ShipmentStatus;
	slaOverdue?: boolean;
}[] = [
	{ key: "all", label: "Toutes" },
	{ key: "PENDING", label: "À préparer", status: "PENDING" },
	{ key: "PICKING", label: "En picking", status: "PICKING" },
	{ key: "READY_TO_SHIP", label: "Prêtes", status: "READY_TO_SHIP" },
	{ key: "overdue", label: "SLA dépassé", slaOverdue: true },
];

function PreparationTab() {
	const [filterKey, setFilterKey] = useState("all");
	const [hub, setHub] = useState("");
	const [page, setPage] = useState(1);
	const { data: hubs } = useHubs();

	const active =
		PREP_FILTERS.find((f) => f.key === filterKey) ?? PREP_FILTERS[0];
	const { data, isLoading } = useShipments({
		hub: hub || undefined,
		status: active.status,
		slaOverdue: active.slaOverdue,
		page,
		pageSize: PAGE_SIZE,
	});

	const items = data?.items ?? [];
	const total = data?.total ?? 0;
	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
	const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
	const to = Math.min(page * PAGE_SIZE, total);

	return (
		<Card>
			<CardHead
				title="File de préparation"
				sub="Triée par échéance SLA (les plus urgentes d'abord)"
				right={
					<select
						style={SELECT_STYLE}
						value={hub}
						onChange={(e) => {
							setHub(e.target.value);
							setPage(1);
						}}
						aria-label="Filtrer par hub"
					>
						<option value="">Tous les hubs</option>
						{(hubs ?? []).map((h) => (
							<option key={h.id} value={h.id}>
								{h.name}
							</option>
						))}
					</select>
				}
			/>
			<div
				style={{
					display: "flex",
					gap: 8,
					flexWrap: "wrap",
					padding: "0 18px 12px",
				}}
			>
				{PREP_FILTERS.map((f) => (
					<ChipFilter
						key={f.key}
						on={filterKey === f.key}
						onClick={() => {
							setFilterKey(f.key);
							setPage(1);
						}}
					>
						{f.label}
					</ChipFilter>
				))}
			</div>
			<div className="table-wrap" style={{ margin: 0 }}>
				<table className="table">
					<thead>
						<tr>
							<th>Colis</th>
							<th>Statut</th>
							<th>Carton</th>
							<th>COD</th>
							<th>Échéance SLA</th>
						</tr>
					</thead>
					<tbody>
						{isLoading && (
							<tr>
								<td
									colSpan={5}
									className="muted"
									style={{ textAlign: "center", padding: 24 }}
								>
									Chargement…
								</td>
							</tr>
						)}
						{!isLoading && items.length === 0 && (
							<tr>
								<td
									colSpan={5}
									className="muted"
									style={{ textAlign: "center", padding: 24 }}
								>
									Aucun colis pour ce filtre.
								</td>
							</tr>
						)}
						{items.map((s: Shipment) => {
							const meta = SHIPMENT_STATUS_META[s.status];
							return (
								<tr key={s.id}>
									<td className="mono">{s.code}</td>
									<td>
										<Tag variant={meta.cls}>{meta.label}</Tag>
									</td>
									<td>{s.cartonSize ?? "—"}</td>
									<td>{s.codAmount ? fmtFCFA(s.codAmount) : "—"}</td>
									<td>
										<Tag variant={slaCls(s.slaDueAt, s.status)}>
											{slaLabel(s.slaDueAt)}
										</Tag>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
			<Pagination
				from={from}
				to={to}
				total={total}
				current={page}
				totalPages={totalPages}
				onChange={setPage}
			/>
		</Card>
	);
}

// ─────────────────────────────────────────────────────────────
// LOG-06 — Suivi tournées (temps réel WebSocket)
// ─────────────────────────────────────────────────────────────

function ToursTab() {
	const { data: tours, isLoading } = useTours({ status: "IN_PROGRESS" });
	const { connected, positions, tourStatuses, stopOutcomes } =
		useLogisticsSocket(true);

	const liveCount = Object.keys(positions).length;
	const outcomeCount = Object.keys(stopOutcomes).length;

	return (
		<>
			<div className="kpi-grid" style={{ marginBottom: 16 }}>
				<div className="kpi accent">
					<div className="kpi-l">Tournées en cours</div>
					<div className="kpi-v tn">{tours?.length ?? 0}</div>
					<div className="kpi-foot muted">statut IN_PROGRESS</div>
				</div>
				<div className="kpi">
					<div className="kpi-l">Livreurs géolocalisés</div>
					<div className="kpi-v tn">{liveCount}</div>
					<div className="kpi-foot muted">positions reçues (live)</div>
				</div>
				<div className="kpi success">
					<div className="kpi-l">Arrêts clôturés</div>
					<div className="kpi-v tn">{outcomeCount}</div>
					<div className="kpi-foot muted">depuis l'ouverture</div>
				</div>
				<div className={`kpi ${connected ? "success" : "warning"}`}>
					<div className="kpi-l">Temps réel</div>
					<div className="kpi-v tn" style={{ fontSize: 18 }}>
						{connected ? "Connecté" : "Hors ligne"}
					</div>
					<div className="kpi-foot muted">
						<span
							style={{
								display: "inline-block",
								width: 8,
								height: 8,
								borderRadius: "50%",
								marginRight: 6,
								background: connected ? "var(--a-success)" : "var(--a-warning)",
							}}
						/>
						WebSocket
					</div>
				</div>
			</div>

			<Card>
				<CardHead
					title="Tournées en cours"
					sub="Mise à jour temps réel via WebSocket — sans rechargement"
				/>
				<div className="table-wrap" style={{ margin: 0 }}>
					<table className="table">
						<thead>
							<tr>
								<th>Tournée</th>
								<th>Livreur</th>
								<th>Véhicule</th>
								<th>Progression</th>
								<th>Position GPS</th>
								<th>Statut</th>
							</tr>
						</thead>
						<tbody>
							{isLoading && (
								<tr>
									<td
										colSpan={6}
										className="muted"
										style={{ textAlign: "center", padding: 24 }}
									>
										Chargement…
									</td>
								</tr>
							)}
							{!isLoading && (tours?.length ?? 0) === 0 && (
								<tr>
									<td
										colSpan={6}
										className="muted"
										style={{ textAlign: "center", padding: 24 }}
									>
										Aucune tournée en cours.
									</td>
								</tr>
							)}
							{(tours ?? []).map((t: Tour) => {
								const live = t.driverId ? positions[t.driverId] : undefined;
								const liveStatus = tourStatuses[t.id];
								const statusKey = liveStatus?.status ?? t.status;
								const meta =
									TOUR_STATUS_META[statusKey] ?? TOUR_STATUS_META.DRAFT;
								const doneStops = (t.stops ?? []).filter(
									(s) => s.outcome !== "PENDING",
								).length;
								const pct = t.totalStops ? (doneStops / t.totalStops) * 100 : 0;
								const driverName =
									t.driver?.user?.fullName ??
									(t.driver?.user
										? `${t.driver.user.firstName} ${t.driver.user.lastName}`
										: "—");
								return (
									<tr key={t.id}>
										<td className="mono">{t.code}</td>
										<td>
											<div
												style={{
													display: "flex",
													alignItems: "center",
													gap: 8,
												}}
											>
												<AdminAvatar name={driverName} />
												{driverName}
											</div>
										</td>
										<td>
											{t.vehicle
												? `${t.vehicle.plate} (${t.vehicle.type})`
												: "—"}
										</td>
										<td>
											<div
												className="tn"
												style={{ fontSize: 11, marginBottom: 3 }}
											>
												{doneStops}/{t.totalStops} arrêts
											</div>
											<div className="bar thin">
												<i
													style={{
														width: pct + "%",
														background: "var(--a-terracotta)",
													}}
												/>
											</div>
										</td>
										<td>
											{live ? (
												<span title={`maj ${relAgo(live.at)}`}>
													{live.lat.toFixed(4)}, {live.lng.toFixed(4)}
													{live.battery !== undefined && (
														<span className="muted"> · 🔋{live.battery}%</span>
													)}
												</span>
											) : (
												<span className="muted">en attente GPS</span>
											)}
										</td>
										<td>
											<Tag variant={meta.cls}>{meta.label}</Tag>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</Card>
		</>
	);
}

// ─────────────────────────────────────────────────────────────
// LOG-11 — KPI logistiques (période sélectionnable)
// ─────────────────────────────────────────────────────────────

type Period = "day" | "week" | "month";

function periodRange(p: Period): { from: string; to: string } {
	const to = new Date();
	const from = new Date();
	if (p === "day") from.setDate(from.getDate() - 1);
	else if (p === "week") from.setDate(from.getDate() - 7);
	else from.setMonth(from.getMonth() - 1);
	return { from: from.toISOString(), to: to.toISOString() };
}

function KpiTab() {
	const [period, setPeriod] = useState<Period>("week");
	const [hub, setHub] = useState("");
	const { data: hubs } = useHubs();
	const range = useMemo(() => periodRange(period), [period]);
	const { data: kpi, isLoading } = useLogisticsKpi({
		from: range.from,
		to: range.to,
		hub: hub || undefined,
	});

	return (
		<>
			<Card>
				<CardHead
					title="Indicateurs de performance"
					sub="Taux SLA, première tentative, délai de préparation"
					right={
						<div
							style={{
								display: "flex",
								gap: 8,
								alignItems: "center",
								flexWrap: "wrap",
							}}
						>
							<Segmented<Period>
								value={period}
								onChange={setPeriod}
								options={[
									{ value: "day", label: "Jour" },
									{ value: "week", label: "Semaine" },
									{ value: "month", label: "Mois" },
								]}
							/>
							<select
								style={SELECT_STYLE}
								value={hub}
								onChange={(e) => setHub(e.target.value)}
								aria-label="Filtrer par hub"
							>
								<option value="">Tous les hubs</option>
								{(hubs ?? []).map((h) => (
									<option key={h.id} value={h.id}>
										{h.name}
									</option>
								))}
							</select>
						</div>
					}
				/>
			</Card>

			<div className="kpi-grid" style={{ marginTop: 16 }}>
				<div className="kpi accent">
					<div className="kpi-l">Taux SLA tenu</div>
					<div className="kpi-v tn">
						{isLoading ? "…" : `${kpi?.slaSuccessRate ?? 0}%`}
					</div>
					<div className="kpi-foot muted">cible An 1 : 75 %</div>
				</div>
				<div className="kpi success">
					<div className="kpi-l">1ʳᵉ tentative réussie</div>
					<div className="kpi-v tn">
						{isLoading ? "…" : `${kpi?.firstAttemptSuccessRate ?? 0}%`}
					</div>
					<div className="kpi-foot muted">cible An 1 : 78 %</div>
				</div>
				<div className="kpi">
					<div className="kpi-l">Délai préparation moyen</div>
					<div className="kpi-v tn">
						{isLoading
							? "…"
							: kpi?.avgPreparationMinutes != null
								? `${kpi.avgPreparationMinutes} min`
								: "—"}
					</div>
					<div className="kpi-foot muted">cible An 1 : ≤ 45 min</div>
				</div>
				<div className="kpi">
					<div className="kpi-l">Taux de livraison</div>
					<div className="kpi-v tn">
						{isLoading ? "…" : `${kpi?.deliveryRate ?? 0}%`}
					</div>
					<div className="kpi-foot muted">
						{isLoading
							? ""
							: `${kpi?.shipments.delivered ?? 0} / ${kpi?.shipments.total ?? 0} colis`}
					</div>
				</div>
			</div>
		</>
	);
}
